import { authorizedFetch } from '../auth/authorizedFetch'
import { ApiError } from './apiError'

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3300'

interface RequestOptions {
  method?: string
  headers?: Record<string, string>
  data?: unknown
  params?: Record<string, unknown>
  /** Set by api2ts for multipart endpoints; `data` is then a FormData. */
  requestType?: 'json' | 'form'
  /** `blob` for downloads (exports). */
  responseType?: 'json' | 'blob'
  /** Public endpoints (published scenes) skip the access token. */
  anonymous?: boolean
  signal?: AbortSignal
  [key: string]: unknown
}

const queryString = (params?: Record<string, unknown>): string => {
  if (!params) return ''
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (!entries.length) return ''
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}`
}

/**
 * The single HTTP client every generated api2ts service calls. Adds the access token, sends JSON or
 * multipart bodies, and turns error answers into `ApiError`s that keep the server's sentence, code
 * and upgrade link — plan refusals are never reduced to "request failed (413)".
 */
export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', headers, data, params, requestType, responseType, anonymous, signal } =
    opts
  const url = `${API_BASE_URL}${path}${queryString(params)}`
  const isForm = requestType === 'form' || data instanceof FormData
  const init: RequestInit = {
    method,
    headers: {
      ...(data != null && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: data == null ? undefined : isForm ? (data as FormData) : JSON.stringify(data),
    // Image generation can legitimately take minutes; anything past this is a stuck request.
    signal: signal ?? AbortSignal.timeout(5 * 60 * 1000),
  }
  if (isForm && init.headers) delete (init.headers as Record<string, string>)['Content-Type']

  const res = anonymous ? await fetch(url, init) : await authorizedFetch(url, init)
  if (!res.ok) throw await ApiError.from(res, method, path)
  if (res.status === 204) return undefined as T
  if (responseType === 'blob') return (await res.blob()) as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}
